import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock, UserCheck } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/shared";
import { CTABand } from "@/pages/Home";
import { API } from "@/lib/api";
import { IMAGES, PACKAGES, PACKAGE_DETAILS } from "@/lib/content";
import { useConsult } from "@/App";

export default function PackagePage() {
  const { packageId } = useParams();
  const pkg = PACKAGES.find((p) => p.id === packageId);
  const details = PACKAGE_DETAILS[packageId];
  const [price, setPrice] = useState(null);
  const { openConsult } = useConsult();

  useEffect(() => {
    if (pkg) {
      API.get("/pricing")
        .then(({ data }) => setPrice(data.find((p) => p.package === pkg.name) || null))
        .catch(() => {});
    }
  }, [pkg]);

  if (!pkg || !details) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center pt-24" data-testid="package-not-found">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-ivory">Package not found</h1>
          <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-sm text-gold-glow" data-testid="back-to-services-link">
            <ArrowLeft className="h-4 w-4" /> All services
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="package-page">
      <section className="relative overflow-hidden pt-40 pb-20">
        <div className="absolute inset-0">
          <img src={IMAGES[details.gallery[0]]} alt="" className="h-full w-full object-cover duotone opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-navy/85 to-navy" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Link to="/services" className="inline-flex items-center gap-2 text-xs font-medium text-gold-glow" data-testid="package-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> All services
            </Link>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Eyebrow>{pkg.tag}</Eyebrow>
              {price && <span className="rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 font-mono text-sm text-gold-glow" data-testid="package-price">{price.price}</span>}
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl tracking-tight text-ivory sm:text-5xl lg:text-6xl" style={{ lineHeight: 1.08 }} data-testid="package-title">
              {pkg.name}
            </h1>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-slate-300 sm:text-xl">{pkg.desc}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <button
              onClick={openConsult}
              data-testid="package-consult-btn"
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light"
            >
              Discuss this package
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="package-scope">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow>What's included</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-3xl tracking-tight text-ivory" style={{ lineHeight: 1.15 }}>Full scope, no surprises</h2>
            </Reveal>
            <div className="mt-8 space-y-4">
              {details.scope.map((s, i) => (
                <Reveal key={s} delay={i * 0.06}>
                  <div className="flex items-start gap-4 rounded-2xl border border-gold/15 bg-navy-surface/50 p-5" data-testid={`scope-item-${i}`}>
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
                      <Check className="h-4 w-4 text-gold-glow" />
                    </div>
                    <p className="pt-1 text-sm font-light text-slate-200">{s}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="space-y-6 lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="gold-ring-card p-8" data-testid="package-timeline">
                <Clock className="h-6 w-6 text-gold" />
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-gold/80">Timeline</p>
                <p className="mt-3 font-serif text-xl leading-snug text-ivory">{details.timeline}</p>
                {price?.note && <p className="mt-3 text-sm font-light text-slate-400">{price.note}</p>}
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="gold-ring-card p-8" data-testid="package-ideal">
                <UserCheck className="h-6 w-6 text-gold" />
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-gold/80">Ideal for</p>
                <p className="mt-3 text-sm font-light leading-relaxed text-slate-300">{details.ideal}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-gold/10 py-20 lg:py-24" data-testid="package-gallery">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><Eyebrow>The standard we build to</Eyebrow></Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {details.gallery.map((key, i) => (
              <Reveal key={key} delay={i * 0.12}>
                <div className="relative">
                  <div className="absolute -inset-2.5 rounded-3xl border border-gold/20" />
                  <img
                    src={IMAGES[key]}
                    alt={`${pkg.name} — reference imagery`}
                    className="relative h-72 w-full rounded-3xl object-cover duotone sm:h-96"
                    data-testid={`gallery-img-${i}`}
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title={`Ready to talk ${pkg.name.toLowerCase()}?`}
        text="Book a free consultation — thirty minutes, plain English, and a fixed price in writing before any work begins."
      />
    </main>
  );
}
